import SplitText from '@activetheory/split-text'
import gsap from 'gsap'
import ScrollTrigger from 'gsap/dist/ScrollTrigger'

export type SharedAnimation = {
  timeline: gsap.core.Timeline
  revert: () => void
}

export const titleRevealAnimation = (
  target: gsap.DOMTarget,
  reverse: boolean
): SharedAnimation => {
  /*------------------------------
  Una SplitText per elemento: la libreria accumula le lines tra gli elementi
  passati alla stessa istanza e rielabora quelle già smontate, quindi con più
  di un target il secondo split va in errore.
  ------------------------------*/
  const splits = gsap.utils.toArray<HTMLElement>(target).map((element) => {
    const splitText = new SplitText(element, {
      type: 'lines',
      noBalance: true
    })
    splitText.lines.map((line) => {
      const lineElement = line as unknown as HTMLElement
      if (lineElement.innerHTML === '') {
        lineElement.innerHTML = '.'
        lineElement.classList.add('opacity-0')
      }
    })
    const splitTextWrap = new SplitText(element, {
      type: 'lines',
      noBalance: true
    })

    return { splitText, splitTextWrap }
  })

  const revert = () => {
    splits.forEach(({ splitText, splitTextWrap }) => {
      splitTextWrap.revert()
      splitText.revert()
    })
  }

  const timeline = gsap.timeline({
    onComplete: () => {
      if (reverse) return
      revert()
    }
  })

  splits.forEach(({ splitTextWrap }) => {
    splitTextWrap.lines.map((line, index) => {
      const lineElement = line as unknown as HTMLElement
      lineElement.classList.add('overflow-hidden')
      return timeline.fromTo(
        lineElement.children,
        { yPercent: 100, scaleY: 1.1 },
        {
          yPercent: 0,
          scaleY: 1,
          ease: 'power3.out',
          duration: 1.2,
          delay: 0.09 * index
        },
        '<'
      )
    })
  })

  return {
    timeline,
    revert
  }
}

export const buttonRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0, y: 40 },
        { opacity: 1, y: 0, duration: 0.8, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const imageRevealAnimation = (
  target: gsap.DOMTarget
): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { opacity: 0, y: 40, scale: 1.1 },
        { opacity: 1, y: 0, scale: 1, duration: 0.8, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const batchRevealAnimation = ({
  targets,
  reverse,
  stagger
}: {
  targets: gsap.DOMTarget
  reverse: boolean
  stagger?: gsap.NumberValue | gsap.StaggerVars
}) => {
  gsap.set(targets, { opacity: 0, y: 30 })
  ScrollTrigger.batch(targets, {
    once: !reverse,
    onEnter: (batch) =>
      gsap.fromTo(
        batch,
        { opacity: 0, y: 30 },
        {
          opacity: 0,
          y: 0,
          duration: 0.6,
          ease: 'power2.out',
          stagger
        }
      )
  })
}

export const fadeInAnimation = (target: gsap.DOMTarget): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { autoAlpha: 0, y: 20 },
        { autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const fadeImageAnimation = (target: gsap.DOMTarget): SharedAnimation => {
  return {
    timeline: gsap
      .timeline()
      .fromTo(
        target,
        { autoAlpha: 0 },
        { autoAlpha: 1, duration: 1, ease: 'power3.out' }
      ),

    revert: () => {
      return
    }
  }
}

export const gridAnimation = (target: gsap.DOMTarget): SharedAnimation => {
  return {
    timeline: gsap.timeline().fromTo(
      target,
      { opacity: 0, y: 40 },
      {
        opacity: 1,
        y: 0,
        duration: 0.8,
        stagger: { amount: 0.2 },
        ease: 'power2.out'
      }
    ),

    revert: () => {
      return
    }
  }
}
